// frontend/src/reducers/returnReducer.js

import * as actionTypes from "../constants/returnConstants";

function createAsyncState(extra = {}) {
  return {
    loading: false,
    success: false,
    error: null,
    errorCode: "",
    requestId: "",
    retryAfterSeconds: 0,
    message: "",
    ...extra,
  };
}

function createInitialState() {
  return {
    list: createAsyncState({
      returnRequests: [],
      page: 1,
      limit: 20,
      pages: 1,
      count: 0,
    }),

    eligibility: createAsyncState({
      orderId: "",
      orderNumber: "",
      reasonCategory: "",
      eligible: false,
      lines: [],
      policy: null,
    }),

    create: createAsyncState({
      returnRequest: null,
    }),

    details: createAsyncState({
      returnRequest: null,
    }),

    cancel: createAsyncState({
      returnRequest: null,
    }),

    currentReturnRequest: null,
  };
}

const initialState = createInitialState();

function requestBranch(branch = {}) {
  return {
    ...branch,
    loading: true,
    success: false,
    error: null,
    errorCode: "",
    requestId: "",
    retryAfterSeconds: 0,
    message: "",
  };
}

function successBranch(branch = {}, payload = {}, extra = {}) {
  return {
    ...branch,
    loading: false,
    success: true,
    error: null,
    errorCode: "",
    requestId: "",
    retryAfterSeconds: 0,
    message: String(payload?.message || ""),
    ...extra,
  };
}

function failBranch(branch = {}, payload = {}) {
  return {
    ...branch,
    loading: false,
    success: false,
    error: String(payload?.message || "Request failed."),
    errorCode: String(payload?.code || ""),
    requestId: String(payload?.requestId || ""),
    retryAfterSeconds: Number(payload?.retryAfterSeconds || 0),
    message: "",
  };
}

function getReturnRequest(payload = {}) {
  return payload?.returnRequest || null;
}

function upsertRequest(list = [], nextItem = null) {
  if (!nextItem) return list;

  const nextId = String(nextItem?._id || nextItem?.id || "");
  if (!nextId) return list;

  const exists = list.some(
    (item) => String(item?._id || item?.id || "") === nextId,
  );

  if (!exists) {
    return [nextItem, ...list];
  }

  return list.map((item) =>
    String(item?._id || item?.id || "") === nextId ? nextItem : item,
  );
}

export function returnReducer(state = initialState, action) {
  switch (action.type) {
    case actionTypes.RETURN_LIST_REQUEST:
      return {
        ...state,
        list: requestBranch(state.list),
      };

    case actionTypes.RETURN_LIST_SUCCESS:
      return {
        ...state,
        list: successBranch(state.list, action.payload, {
          returnRequests: Array.isArray(action.payload?.returnRequests)
            ? action.payload.returnRequests
            : [],
          page: Number(action.payload?.page || 1),
          limit: Number(action.payload?.limit || 20),
          pages: Number(action.payload?.pages || 1),
          count: Number(action.payload?.count || 0),
        }),
      };

    case actionTypes.RETURN_LIST_FAIL:
      return {
        ...state,
        list: failBranch(state.list, action.payload),
      };

    case actionTypes.RETURN_LIST_RESET:
      return {
        ...state,
        list: initialState.list,
      };

    case actionTypes.RETURN_ELIGIBILITY_REQUEST:
      return {
        ...state,
        eligibility: requestBranch(state.eligibility),
      };

    case actionTypes.RETURN_ELIGIBILITY_SUCCESS:
      return {
        ...state,
        eligibility: successBranch(state.eligibility, action.payload, {
          orderId: String(action.payload?.orderId || ""),
          orderNumber: String(action.payload?.orderNumber || ""),
          reasonCategory: String(action.payload?.reasonCategory || ""),
          eligible: Boolean(action.payload?.eligible),
          lines: Array.isArray(action.payload?.lines)
            ? action.payload.lines
            : [],
          policy: action.payload?.policy || null,
        }),
      };

    case actionTypes.RETURN_ELIGIBILITY_FAIL:
      return {
        ...state,
        eligibility: failBranch(state.eligibility, action.payload),
      };

    case actionTypes.RETURN_ELIGIBILITY_RESET:
      return {
        ...state,
        eligibility: initialState.eligibility,
      };

    case actionTypes.RETURN_CREATE_REQUEST:
      return {
        ...state,
        create: requestBranch(state.create),
      };

    case actionTypes.RETURN_CREATE_SUCCESS: {
      const returnRequest = getReturnRequest(action.payload);

      return {
        ...state,
        create: successBranch(state.create, action.payload, {
          returnRequest,
        }),
        currentReturnRequest: returnRequest,
        list: {
          ...state.list,
          returnRequests: upsertRequest(
            state.list.returnRequests,
            returnRequest,
          ),
        },
      };
    }

    case actionTypes.RETURN_CREATE_FAIL:
      return {
        ...state,
        create: failBranch(state.create, action.payload),
      };

    case actionTypes.RETURN_CREATE_RESET:
      return {
        ...state,
        create: initialState.create,
      };

    case actionTypes.RETURN_DETAILS_REQUEST:
      return {
        ...state,
        details: requestBranch(state.details),
      };

    case actionTypes.RETURN_DETAILS_SUCCESS: {
      const returnRequest = getReturnRequest(action.payload);

      return {
        ...state,
        details: successBranch(state.details, action.payload, {
          returnRequest,
        }),
        currentReturnRequest: returnRequest,
        list: {
          ...state.list,
          returnRequests: upsertRequest(
            state.list.returnRequests,
            returnRequest,
          ),
        },
      };
    }

    case actionTypes.RETURN_DETAILS_FAIL:
      return {
        ...state,
        details: failBranch(state.details, action.payload),
      };

    case actionTypes.RETURN_DETAILS_RESET:
      return {
        ...state,
        details: initialState.details,
      };

    case actionTypes.RETURN_CANCEL_REQUEST:
      return {
        ...state,
        cancel: requestBranch(state.cancel),
      };

    case actionTypes.RETURN_CANCEL_SUCCESS: {
      const returnRequest = getReturnRequest(action.payload);

      return {
        ...state,
        cancel: successBranch(state.cancel, action.payload, {
          returnRequest,
        }),
        currentReturnRequest: returnRequest || state.currentReturnRequest,
        list: {
          ...state.list,
          returnRequests: upsertRequest(
            state.list.returnRequests,
            returnRequest,
          ),
        },
      };
    }

    case actionTypes.RETURN_CANCEL_FAIL:
      return {
        ...state,
        cancel: failBranch(state.cancel, action.payload),
      };

    case actionTypes.RETURN_CANCEL_RESET:
      return {
        ...state,
        cancel: initialState.cancel,
      };

    case actionTypes.RETURN_RESET_CURRENT:
      return {
        ...state,
        currentReturnRequest: null,
      };

    case actionTypes.RETURN_RESET_ERROR:
      return {
        ...state,
        list: { ...state.list, error: null, errorCode: "" },
        eligibility: { ...state.eligibility, error: null, errorCode: "" },
        create: { ...state.create, error: null, errorCode: "" },
        details: { ...state.details, error: null, errorCode: "" },
        cancel: { ...state.cancel, error: null, errorCode: "" },
      };

    case actionTypes.RETURN_RESET_META:
      return {
        ...state,
        list: {
          ...state.list,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
        eligibility: {
          ...state.eligibility,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
        create: {
          ...state.create,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
        details: {
          ...state.details,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
        cancel: {
          ...state.cancel,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
      };

    case actionTypes.RETURN_RESET_STATE:
      return initialState;

    default:
      return state;
  }
}

export default returnReducer;
